import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatRego } from '@/lib/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { 
  Car, 
  User, 
  Phone, 
  Mail, 
  CheckCircle, 
  Calendar,
  Clock,
  Loader2,
  AlertCircle,
  Camera,
  X,
  CreditCard,
  Home,
  QrCode,
  Smartphone,
  ExternalLink,
  Key
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

// Service types available for booking
const SERVICE_TYPES = [
  { value: 'service', label: 'General Service' },
  { value: 'rwc', label: 'RWC (Roadworthy)' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'aircon', label: 'Aircon' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' }
];

// Time slots (8am to 12pm only)
const TIME_SLOTS = [
  { value: '08:00', label: '8:00 AM' },
  { value: '08:30', label: '8:30 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '09:30', label: '9:30 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' }
];

export default function CustomerBooking() {
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('site') || '';
  
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [dayFull, setDayFull] = useState(false);
  const [bookingsCount, setBookingsCount] = useState(0);
  
  // Form data — iter115 split full name → first_name + last_name and
  // require mobile + address + make + model + rego on the public booking
  // page to keep parity with the kiosk walk-in form.
  const [formData, setFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    vehicle_rego: '',
    vehicle_make: '',
    vehicle_model: '',
    comments: '',
    scheduled_date: '',
    preferred_time: '09:00',
    loan_car_requested: false,
    customer_license_number: ''
  });
  
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [licensePhoto, setLicensePhoto] = useState(null);
  const [licensePhotoPreview, setLicensePhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // For QR code flow when loan car is requested
  const [loanCarToken, setLoanCarToken] = useState(null);
  const [loanCarSubmitted, setLoanCarSubmitted] = useState(false);
  const [checkingLoanStatus, setCheckingLoanStatus] = useState(false);

  // Check booking availability for selected date
  const checkDateAvailability = useCallback(async (date) => {
    if (!date) return;
    
    setCheckingAvailability(true);
    try {
      const url = siteId 
        ? `${BACKEND_URL}/api/bookings/public/availability?date=${date}&site_id=${siteId}`
        : `${BACKEND_URL}/api/bookings/public/availability?date=${date}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setBookingsCount(data.current_bookings || 0);
        setDayFull(data.is_full || false);
        if (data.is_full) {
          toast.warning(`This date is fully booked (${data.current_bookings}/${data.max_bookings}). Please select another date.`);
        }
      }
    } catch (error) {
      console.error('Failed to check availability:', error);
    } finally {
      setCheckingAvailability(false);
    }
  }, [siteId]);

  // Reset form for new booking
  const resetForNewBooking = useCallback(() => {
    setFormData({
      customer_first_name: '',
      customer_last_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      vehicle_rego: '',
      vehicle_make: '',
      vehicle_model: '',
      comments: '',
      scheduled_date: '',
      preferred_time: '09:00',
      loan_car_requested: false,
      customer_license_number: ''
    });
    setSelectedServiceTypes([]);
    setLicensePhoto(null);
    setLicensePhotoPreview(null);
    setLoanCarToken(null);
    setLoanCarSubmitted(false);
    setBookingComplete(false);
    setBookingResult(null);
    setDayFull(false);
    setBookingsCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Auto-reset timer for kiosk mode (10 seconds after booking complete)
  const [resetCountdown, setResetCountdown] = useState(null);
  
  useEffect(() => {
    if (bookingComplete) {
      setResetCountdown(10);
    }
  }, [bookingComplete]);

  useEffect(() => {
    if (resetCountdown === null) return;
    
    if (resetCountdown <= 0) {
      resetForNewBooking();
      setResetCountdown(null);
      return;
    }

    const timer = setTimeout(() => {
      setResetCountdown(resetCountdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resetCountdown, resetForNewBooking]);

  // Handle date change
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setFormData(prev => ({ ...prev, scheduled_date: newDate }));
    if (newDate) {
      checkDateAvailability(newDate);
    }
  };

  // Handle license photo upload
  const handleLicensePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setLicensePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLicensePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      toast.success('License photo uploaded');
    }
  };

  // Remove license photo
  const removeLicensePhoto = () => {
    setLicensePhoto(null);
    setLicensePhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Generate a loan car token for QR code flow
  const generateLoanCarToken = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/public/loan-car-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: `${formData.customer_first_name} ${formData.customer_last_name}`.trim() || 'Customer',
          customer_phone: formData.customer_phone || '',
          vehicle_rego: formData.vehicle_rego || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLoanCarToken(data.token);
        setLoanCarSubmitted(false);
        return data.token;
      }
    } catch (error) {
      console.error('Failed to generate loan car token:', error);
    }
    return null;
  };

  // Check if loan car details have been submitted via QR
  const checkLoanCarStatus = useCallback(async () => {
    if (!loanCarToken || loanCarSubmitted) return;
    
    setCheckingLoanStatus(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/public/loan-car-token/${loanCarToken}/status`);
      if (response.ok) {
        const data = await response.json();
        if (data.submitted) {
          setLoanCarSubmitted(true);
          setFormData(prev => ({
            ...prev,
            customer_license_number: data.license_number || prev.customer_license_number,
            // Split incoming combined name back into first/last if the
            // loan-car QR endpoint sent a `customer_name`. Keep existing
            // values if already entered.
            customer_first_name: prev.customer_first_name || (data.customer_name ? data.customer_name.split(/\s+/)[0] : prev.customer_first_name),
            customer_last_name: prev.customer_last_name || (data.customer_name ? data.customer_name.split(/\s+/).slice(1).join(' ') : prev.customer_last_name),
            customer_phone: data.customer_phone || prev.customer_phone,
            customer_email: data.customer_email || prev.customer_email,
            customer_address: data.customer_address || prev.customer_address
          }));
          toast.success('License details received!');
        }
      }
    } catch (error) {
      console.error('Failed to check loan car status:', error);
    } finally {
      setCheckingLoanStatus(false);
    }
  }, [loanCarToken, loanCarSubmitted]);

  // Poll for loan car submission when QR is shown
  useEffect(() => {
    if (!formData.loan_car_requested || !loanCarToken || loanCarSubmitted) return;
    
    const interval = setInterval(checkLoanCarStatus, 3000); // Check every 3 seconds
    return () => clearInterval(interval);
  }, [formData.loan_car_requested, loanCarToken, loanCarSubmitted, checkLoanCarStatus]);

  // Handle loan car toggle
  const handleLoanCarToggle = async (checked) => {
    setFormData(prev => ({ ...prev, loan_car_requested: checked }));
    
    if (checked && !loanCarToken) {
      await generateLoanCarToken();
    }
  };

  // Handle service type toggle
  const handleServiceTypeToggle = useCallback((type) => {
    setSelectedServiceTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  }, []);

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Validate form — iter115 requires First Name + Last Name + Mobile +
  // Address + Make + Model + Rego before submission (matches kiosk
  // walk-in form so data quality stays consistent across channels).
  const validateForm = () => {
    const missing = [];
    if (!formData.customer_first_name.trim()) missing.push('First name');
    if (!formData.customer_last_name.trim()) missing.push('Last name');
    if (!formData.customer_phone.trim()) missing.push('Mobile');
    if (!formData.customer_address.trim()) missing.push('Address');
    if (!formData.vehicle_make.trim()) missing.push('Make');
    if (!formData.vehicle_model.trim()) missing.push('Model');
    if (!formData.vehicle_rego.trim()) missing.push('Rego');
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      return false;
    }
    if (selectedServiceTypes.length === 0) {
      toast.error('Please select at least one service');
      return false;
    }
    if (!formData.scheduled_date) {
      toast.error('Please select a date');
      return false;
    }
    if (dayFull) {
      toast.error('This date is fully booked. Please select another date.');
      return false;
    }
    return true;
  };

  // Submit booking
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const bookedDatetime = `${formData.scheduled_date}T${formData.preferred_time}:00`;
      
      const payload = {
        service_types: selectedServiceTypes,
        comments: formData.comments || null,
        // Send both split fields AND the combined `customer_name` so the
        // public-booking BE endpoint (which currently keys on customer_name)
        // keeps working until we update its schema.
        customer_first_name: formData.customer_first_name.trim(),
        customer_last_name: formData.customer_last_name.trim(),
        customer_name: `${formData.customer_first_name.trim()} ${formData.customer_last_name.trim()}`.trim(),
        customer_phone: formData.customer_phone.trim(),
        customer_email: formData.customer_email.trim() || null,
        customer_address: formData.customer_address.trim() || null,
        vehicle_rego: formData.vehicle_rego.toUpperCase().trim(),
        vehicle_make: formData.vehicle_make.trim() || null,
        vehicle_model: formData.vehicle_model.trim() || null,
        loan_car_requested: formData.loan_car_requested,
        loan_car_token: loanCarToken || null,
        customer_license_number: formData.customer_license_number.trim() || null,
        booked_datetime: bookedDatetime,
        terms_accepted: true
      };

      const url = siteId 
        ? `${BACKEND_URL}/api/bookings/public?site_id=${siteId}`
        : `${BACKEND_URL}/api/bookings/public`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const booking = await response.json();
        setBookingResult(booking);
        setBookingComplete(true);
        toast.success('Booking created successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create booking' }));
        toast.error(errorData.detail || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Booking complete screen
  if (bookingComplete && bookingResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <Toaster position="top-center" richColors />
        <div className="max-w-md mx-auto">
          <Card className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h1>
            <p className="text-slate-600 mb-6">Your service has been scheduled.</p>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Booking #:</span>
                <span className="font-semibold">{bookingResult.booking_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Vehicle:</span>
                <span className="font-semibold">{formData.vehicle_rego.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date:</span>
                <span className="font-semibold">
                  {new Date(formData.scheduled_date).toLocaleDateString('en-AU', { 
                    weekday: 'short', day: 'numeric', month: 'short' 
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Time:</span>
                <span className="font-semibold">
                  {TIME_SLOTS.find(t => t.value === formData.preferred_time)?.label}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Services:</span>
                <span className="font-semibold text-right">
                  {selectedServiceTypes.map(t => SERVICE_TYPES.find(s => s.value === t)?.label).join(', ')}
                </span>
              </div>
            </div>
            
            <p className="text-sm text-slate-500 mb-4">
              We'll contact you at <strong>{formData.customer_phone}</strong> to confirm.
            </p>
            
            <Button 
              onClick={resetForNewBooking}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700"
              data-testid="new-booking-btn"
            >
              <Car className="h-4 w-4 mr-2" />
              Book Another Service
            </Button>
            
            <p className="text-xs text-slate-400 mt-4">
              This form will reset in <span className="font-bold text-blue-600">{resetCountdown}</span> seconds
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6 px-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center mx-auto mb-3">
            <Car className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Book Your Service</h1>
          <p className="text-slate-500 text-sm mt-1">Fill in your details to schedule a service</p>
        </div>

        <Card className="p-5">
          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/* Customer Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <User className="h-4 w-4 text-blue-600" />
                Your Details
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Input
                    value={formData.customer_first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_first_name: e.target.value }))}
                    placeholder="First Name *"
                    className="h-11"
                    data-testid="customer-first-name-input"
                    required
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Input
                    value={formData.customer_last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_last_name: e.target.value }))}
                    placeholder="Last Name *"
                    className="h-11"
                    data-testid="customer-last-name-input"
                    required
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => {
                      // Live AU phone formatter — matches kiosk behaviour.
                      let v = e.target.value;
                      const digits = v.replace(/\D/g, '');
                      if (/^\s*[+\d\s]+\s*$/.test(v) && digits.length >= 4) {
                        if (digits.startsWith('61') && digits.length >= 11) {
                          const local = '0' + digits.slice(2);
                          v = local.slice(0, 4)
                            + (local.length > 4 ? ' ' + local.slice(4, 7) : '')
                            + (local.length > 7 ? ' ' + local.slice(7, 10) : '');
                        } else if (digits.startsWith('0') && digits.length === 10) {
                          v = digits.slice(0, 4) + ' ' + digits.slice(4, 7) + ' ' + digits.slice(7, 10);
                        } else if (digits.startsWith('0') && digits.length >= 4) {
                          v = digits.slice(0, 4)
                            + (digits.length > 4 ? ' ' + digits.slice(4, Math.min(digits.length, 7)) : '')
                            + (digits.length > 7 ? ' ' + digits.slice(7, 10) : '');
                        }
                      }
                      setFormData(prev => ({ ...prev, customer_phone: v }));
                    }}
                    placeholder="Mobile *"
                    className="h-11"
                    data-testid="customer-phone-input"
                    required
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                    placeholder="Email (optional)"
                    className="h-11"
                    data-testid="customer-email-input"
                  />
                </div>
              </div>

              <Input
                value={formData.customer_address}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                placeholder="Street Address *"
                className="h-11"
                data-testid="customer-address-input"
                required
              />
            </div>

            {/* Vehicle Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Car className="h-4 w-4 text-blue-600" />
                Vehicle Details
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <Input
                  value={formData.vehicle_rego}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicle_rego: formatRego(e.target.value) }))}
                  placeholder="Rego *"
                  className="h-11 uppercase font-mono"
                  maxLength={7}
                  data-testid="vehicle-rego-input"
                  required
                />
                <Input
                  value={formData.vehicle_make}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicle_make: e.target.value }))}
                  placeholder="Make *"
                  className="h-11"
                  data-testid="vehicle-make-input"
                  required
                />
                <Input
                  value={formData.vehicle_model}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                  placeholder="Model *"
                  className="h-11"
                  data-testid="vehicle-model-input"
                  required
                />
              </div>
            </div>

            {/* Service Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                Select Services *
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                {SERVICE_TYPES.map((service) => (
                  <button
                    key={service.value}
                    type="button"
                    onClick={() => handleServiceTypeToggle(service.value)}
                    className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                      selectedServiceTypes.includes(service.value)
                        ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`service-type-${service.value}`}
                  >
                    {service.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date & Time */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Calendar className="h-4 w-4 text-blue-600" />
                Preferred Date & Time
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={handleDateChange}
                    min={getMinDate()}
                    className="h-11"
                    data-testid="scheduled-date-input"
                  />
                  {checkingAvailability && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    </div>
                  )}
                </div>
                <select
                  value={formData.preferred_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, preferred_time: e.target.value }))}
                  className="h-11 rounded-md border border-slate-200 px-3 text-sm"
                  data-testid="preferred-time-select"
                >
                  {TIME_SLOTS.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
              
              {dayFull && (
                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>This date is fully booked ({bookingsCount}/20). Please select another date.</span>
                </div>
              )}
              
              {formData.scheduled_date && !dayFull && bookingsCount > 0 && (
                <p className="text-xs text-slate-500">
                  {20 - bookingsCount} spots remaining for this date
                </p>
              )}
            </div>

            {/* Loan Car Option */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Car className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-slate-700">Need a loan car?</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.loan_car_requested}
                    onChange={(e) => handleLoanCarToggle(e.target.checked)}
                    className="sr-only peer"
                    data-testid="loan-car-checkbox"
                  />
                  <div className="w-10 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {/* Loan Car Info - Shows when enabled */}
              {formData.loan_car_requested && (
                <div className="border-2 border-cyan-200 bg-cyan-50/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-cyan-700">
                    <Key className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Loan Car Allocated</p>
                      <p className="text-sm text-cyan-600">A loan car will be reserved for this booking. Details can be added when the customer arrives.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Comments */}
            <Textarea
              value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              placeholder="Any additional notes or specific issues..."
              rows={2}
              className="text-sm"
            />

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting || dayFull}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-base font-semibold"
              data-testid="submit-booking-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Creating Booking...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Book Service
                </>
              )}
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-4">
          By booking, you agree to our terms and conditions.
        </p>
      </div>
    </div>
  );
}
